import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProfile } from "@/context/ProfileContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import ProfileAvatar from "@/components/ProfileAvatar";
import ContentRow from "@/components/ContentRow";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Forbidden, NotFound } from "@/pages/ErrorPages";
import { formatMoney, intervalLabel } from "@/lib/money";
import {
  LogOut, CreditCard, Play, Home, Sparkles, Search, ListVideo, Users,
  ArrowRight, ArrowUpRight, Check, RefreshCw, ShieldCheck, Clapperboard, Calendar,
} from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
}

export default function Account() {
  const { slug, sp } = useStore();
  const navigate = useNavigate();
  const { user, status, logout } = useAuth();
  const { activeProfile, clearActiveProfile } = useProfile();

  const [tenant, setTenant] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [progress, setProgress] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [pageStatus, setPageStatus] = useState("loading"); // loading | ready | not_found | error
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setPageStatus("loading");
    try {
      const t = await api.get(`/public/tenants/${slug}`);
      setTenant(t.data);
      // Member data — tolerate individual failures
      const [sub, prog, wl, tx] = await Promise.allSettled([
        api.get("/me/subscription"),
        api.get("/me/progress"),
        api.get("/me/watchlist"),
        api.get("/me/transactions"),
      ]);
      if (sub.status === "fulfilled") setSubscription(sub.value.data?.subscription ?? null);
      if (prog.status === "fulfilled") setProgress(prog.value.data || []);
      if (wl.status === "fulfilled") setWatchlist(wl.value.data || []);
      if (tx.status === "fulfilled") setTransactions(tx.value.data || []);
      setPageStatus("ready");
    } catch (e) {
      if (e?.response?.status === 404) setPageStatus("not_found");
      else setPageStatus("error");
    }
  }, [slug]);

  useEffect(() => { if (status === "authenticated") load(); }, [status, load]);

  const continueWatching = useMemo(() => {
    if (!tenant) return [];
    return (progress || [])
      .filter((p) => p.content && p.content.tenant_id === tenant.id && !p.completed && p.position_seconds > 5)
      .slice(0, 12)
      .map((p) => ({
        ...p.content,
        _progress_pct: p.duration_seconds > 0 ? (p.position_seconds / p.duration_seconds) * 100 : 0,
      }));
  }, [progress, tenant]);

  const myList = useMemo(() => {
    if (!tenant) return [];
    return (watchlist || [])
      .filter((w) => w.content && w.content.tenant_id === tenant.id)
      .map((w) => w.content);
  }, [watchlist, tenant]);

  // ---- guards ----
  if (status === "loading") return null;
  if (status !== "authenticated" || !user) return <Navigate to={sp(`/login`)} replace />;
  if (pageStatus === "not_found") return <NotFound />;
  if (tenant && user.role === "subscriber" && user.tenant_id && user.tenant_id !== tenant.id) return <Forbidden />;

  const themeColor = tenant?.theme_color || "#7C3AED";
  const accentColor = tenant?.accent_color || "#F59E0B";

  if (pageStatus === "loading" || !tenant) {
    return (
      <StorefrontLayout tenant={tenant || { name: "" }} currentUser={user} onLogout={logout} showSubscriberNav heroBleed>
        <AccountSkeleton />
      </StorefrontLayout>
    );
  }

  if (pageStatus === "error") {
    return (
      <StorefrontLayout tenant={tenant} currentUser={user} onLogout={logout} showSubscriberNav>
        <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="account-error-alert">
          <h1 className="font-display font-bold text-3xl tracking-tight">Something went wrong</h1>
          <p className="mt-3 text-white/55 text-sm">We couldn't load your account right now.</p>
          <Button onClick={load} className="mt-6 rounded-none bg-white text-black hover:bg-white/90 uppercase text-[11px] tracking-[0.25em]" data-testid="retry-button">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
          </Button>
        </div>
      </StorefrontLayout>
    );
  }

  const displayName = activeProfile?.name || user.full_name || "there";
  const resumeItem = continueWatching[0];

  const onCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post("/me/subscription/cancel");
      setSubscription(data);
      toast.success("Auto-renew turned off. You keep access until the period ends.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleSwitchProfile = () => { clearActiveProfile(); navigate(sp(`/select-profile`)); };
  const handleLogout = () => { clearActiveProfile(); logout(); navigate(sp(``)); };

  return (
    <StorefrontLayout tenant={tenant} currentUser={user} onLogout={logout} showSubscriberNav heroBleed>
      {/* ===== MEMBER HERO ===== */}
      <section className="relative overflow-hidden grain" data-testid="member-hero">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              `radial-gradient(60% 90% at 12% 0%, color-mix(in srgb, ${themeColor} 30%, transparent), transparent 70%),` +
              `radial-gradient(45% 70% at 92% 8%, color-mix(in srgb, ${accentColor} 18%, transparent), transparent 70%),` +
              `linear-gradient(180deg, #0b0b0c 0%, #0a0a0a 100%)`,
          }}
        />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-14 md:pb-20">
          <div className="flex flex-col md:flex-row md:items-end gap-6 md:gap-8">
            <ProfileAvatar profile={activeProfile || { name: displayName, avatar_key: "violet" }} size="xl" />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.4em] text-white/45">{tenant.name} · Membership</p>
              <h1 className="mt-2 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95]" data-testid="account-welcome-name">
                Welcome back,<br />
                <span style={{ color: accentColor }}>{displayName}.</span>
              </h1>
              <p className="mt-3 text-white/55 text-sm md:text-base">{user.email}</p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {resumeItem ? (
              <Link
                to={sp(`/watch/${resumeItem.slug}`)}
                className="inline-flex items-center gap-2 px-7 h-12 text-sm font-bold uppercase tracking-[0.2em] bg-white text-black hover:bg-white/90 transition-colors"
                data-testid="member-hero-primary-cta-button"
              >
                <Play className="w-4 h-4 fill-black" /> Resume watching
              </Link>
            ) : (
              <Link
                to={sp(`/home`)}
                className="inline-flex items-center gap-2 px-7 h-12 text-sm font-bold uppercase tracking-[0.2em] bg-white text-black hover:bg-white/90 transition-colors"
                data-testid="member-hero-primary-cta-button"
              >
                <Clapperboard className="w-4 h-4" /> Browse catalogue
              </Link>
            )}
            <button
              onClick={() => navigate(sp(`/profiles`))}
              className="inline-flex items-center gap-2 px-6 h-12 text-sm font-bold uppercase tracking-[0.2em] border border-white/25 bg-white/5 backdrop-blur-md hover:bg-white/10 text-white transition-colors"
              data-testid="member-hero-manage-profiles-button"
            >
              <Users className="w-4 h-4" /> Manage profiles
            </button>
          </div>
        </div>
      </section>

      {/* ===== MAIN GRID ===== */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-8 grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-6 lg:gap-8">
        {/* LEFT: subscription + quick links */}
        <div className="space-y-6">
          <SubscriptionCard
            subscription={subscription}
            themeColor={themeColor}
            accentColor={accentColor}
            sp={sp}
            onCancel={onCancel}
            cancelling={cancelling}
          />
        </div>

        {/* RIGHT: quick links */}
        <div className="space-y-6">
          <QuickLinks sp={sp} onSwitchProfile={handleSwitchProfile} />
        </div>
      </div>

      {/* ===== RAILS ===== */}
      <div className="max-w-[1400px] mx-auto pb-2">
        {continueWatching.length > 0 ? (
          <div data-testid="continue-watching-rail">
            <ContentRow name="Continue watching" items={continueWatching} tenantSlug={slug} themeColor={themeColor} kind="mixed" />
          </div>
        ) : (
          <div className="px-6 md:px-10 py-4">
            <EmptyState
              testid="empty-continue-watching"
              icon={Play}
              accentColor={accentColor}
              title="Your next watch is waiting"
              body="Start something new — we'll keep your place so you can pick up right where you left off."
              ctaLabel="Browse catalogue"
              ctaTestid="empty-continue-watching-cta"
              onCta={() => navigate(sp(`/home`))}
            />
          </div>
        )}

        {myList.length > 0 ? (
          <div data-testid="my-list-rail">
            <ContentRow name="My list" items={myList} tenantSlug={slug} themeColor={themeColor} kind="mixed" />
          </div>
        ) : (
          <div className="px-6 md:px-10 py-4">
            <EmptyState
              testid="empty-my-list"
              icon={ListVideo}
              accentColor={accentColor}
              title="Save it for later"
              body="Tap the + on any title and it lands here, ready whenever you are."
              ctaLabel="Explore titles"
              ctaTestid="empty-my-list-cta"
              onCta={() => navigate(sp(`/home`))}
            />
          </div>
        )}
      </div>

      {/* ===== BILLING PREVIEW ===== */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8">
        <BillingPreview transactions={transactions} sp={sp} accentColor={accentColor} />
      </div>

      {/* ===== SIGN OUT ===== */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        <Separator className="bg-white/10" />
        <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm text-white/60">Signed in as <span className="text-white">{user.email}</span></p>
            <p className="text-xs text-white/35 mt-1">Member since {fmtDate(user.created_at)}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="rounded-none border border-white/15 text-white/80 hover:text-white hover:bg-white/5 uppercase text-[11px] tracking-[0.25em]"
            data-testid="sign-out-button"
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
          </Button>
        </div>
      </div>
    </StorefrontLayout>
  );
}

/* ---------------- Subscription status card ---------------- */
function SubscriptionCard({ subscription, themeColor, accentColor, sp, onCancel, cancelling }) {
  const sub = subscription;
  const active = sub && (sub.status === "active" || sub.status === "trialing");
  const willCancel = active && sub.auto_renew === false;

  if (!active) {
    return (
      <div
        className="relative overflow-hidden bg-[#121212]/80 ring-1 ring-white/10 p-7 md:p-9"
        style={{ borderLeft: `2px solid color-mix(in srgb, ${accentColor} 70%, transparent)` }}
        data-testid="empty-no-subscription"
      >
        <div className="absolute inset-0 -z-10" style={{ background: `radial-gradient(50% 80% at 100% 0%, color-mix(in srgb, ${themeColor} 20%, transparent), transparent 70%)` }} />
        <Badge className="rounded-none uppercase text-[10px] tracking-[0.25em] bg-white/10 text-white/70">No plan yet</Badge>
        <h2 className="mt-4 font-display font-black text-3xl md:text-4xl tracking-tighter">Unlock the full catalogue</h2>
        <p className="mt-3 text-white/60 text-sm max-w-md">One subscription, every title. Stream instantly across your devices — cancel anytime.</p>
        <ul className="mt-5 space-y-2 text-sm text-white/70">
          {["Full catalogue access", "HD streaming", "Cancel anytime"].map((f) => (
            <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4" style={{ color: accentColor }} /> {f}</li>
          ))}
        </ul>
        <Link
          to={sp(`/plans`)}
          className="mt-7 inline-flex items-center gap-2 px-7 h-12 text-sm font-bold uppercase tracking-[0.2em] transition-transform hover:-translate-y-0.5"
          style={{ backgroundColor: accentColor, color: "#0a0a0a" }}
          data-testid="empty-no-subscription-cta"
        >
          View plans <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const plan = sub.plan;
  return (
    <div
      className="relative overflow-hidden bg-[#121212]/80 ring-1 ring-white/10 p-7 md:p-9"
      style={{ borderLeft: `2px solid color-mix(in srgb, ${themeColor} 70%, transparent)` }}
      data-testid="subscription-status-card"
    >
      <div className="absolute inset-0 -z-10" style={{ background: `radial-gradient(50% 80% at 100% 0%, color-mix(in srgb, ${themeColor} 18%, transparent), transparent 70%)` }} />
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.35em] text-white/40">Your subscription</p>
          <h2 className="mt-2 font-display font-black text-3xl md:text-4xl tracking-tighter" data-testid="subscription-plan-name">
            {plan?.name || "Active plan"}
          </h2>
        </div>
        <Badge
          className={`rounded-none uppercase text-[10px] tracking-[0.25em] ${willCancel ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}
          data-testid="subscription-status-badge"
        >
          {willCancel ? "Cancelling" : "Active"}
        </Badge>
      </div>

      {plan && (
        <div className="mt-4 flex items-baseline gap-1.5">
          <span className="font-display font-black text-4xl">{formatMoney(plan.price_amount, plan.currency)}</span>
          <span className="text-white/45 text-sm">{intervalLabel(plan.interval)}</span>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="flex items-center gap-2.5 border border-white/10 px-4 py-3">
          <Calendar className="w-4 h-4 text-white/40" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{willCancel ? "Access until" : "Renews on"}</div>
            <div className="text-white" data-testid="subscription-renewal-date">{fmtDate(sub.current_period_end)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2.5 border border-white/10 px-4 py-3" data-testid="subscription-auto-renew-indicator">
          <RefreshCw className="w-4 h-4 text-white/40" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Auto-renew</div>
            <div className="text-white">{sub.auto_renew ? "On" : "Off"}</div>
          </div>
        </div>
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <Link
          to={sp(`/account/billing`)}
          className="inline-flex items-center gap-2 px-6 h-11 text-xs font-bold uppercase tracking-[0.2em] transition-transform hover:-translate-y-0.5"
          style={{ backgroundColor: accentColor, color: "#0a0a0a" }}
          data-testid="subscription-manage-button"
        >
          <CreditCard className="w-4 h-4" /> Manage billing
        </Link>
        {!willCancel && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="rounded-none border border-white/15 text-white/70 hover:text-white hover:bg-white/5 uppercase text-[11px] tracking-[0.2em] h-11" data-testid="subscription-cancel-button">
                Cancel plan
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#0d0d0d] border-white/10 rounded-none text-white">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display font-bold text-2xl tracking-tight">Turn off auto-renew?</AlertDialogTitle>
                <AlertDialogDescription className="text-white/55">
                  You'll keep full access until {fmtDate(sub.current_period_end)}. After that, your subscription won't renew.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none border-white/15 bg-transparent text-white/70 hover:bg-white/5 hover:text-white uppercase text-[11px] tracking-[0.2em]">Keep plan</AlertDialogCancel>
                <AlertDialogAction onClick={onCancel} disabled={cancelling} className="rounded-none bg-white text-black hover:bg-white/90 uppercase text-[11px] tracking-[0.2em]" data-testid="subscription-cancel-confirm">
                  {cancelling ? "Cancelling…" : "Turn off auto-renew"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

/* ---------------- Quick links ---------------- */
function QuickLinks({ sp, onSwitchProfile }) {
  const items = [
    { key: "home", label: "Home", icon: Home, to: sp(`/home`) },
    { key: "reels", label: "Reels", icon: Sparkles, to: sp(`/reels`) },
    { key: "search", label: "Search", icon: Search, to: sp(`/search`) },
    { key: "my-list", label: "My list", icon: ListVideo, to: sp(`/my-list`) },
    { key: "billing", label: "Billing", icon: CreditCard, to: sp(`/account/billing`) },
    { key: "switch-profile", label: "Switch profile", icon: Users, action: onSwitchProfile },
  ];
  return (
    <div className="bg-[#121212]/60 ring-1 ring-white/10 p-6" data-testid="account-quick-links">
      <p className="text-[11px] uppercase tracking-[0.35em] text-white/40 mb-4 flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5" /> Quick actions
      </p>
      <div className="grid grid-cols-2 gap-3">
        {items.map(({ key, label, icon: Icon, to, action }) => {
          const inner = (
            <>
              <Icon className="w-5 h-5 text-white/80 group-hover:text-white transition-colors" />
              <span className="text-xs uppercase tracking-[0.15em] text-white/70 group-hover:text-white transition-colors">{label}</span>
              <ArrowUpRight className="w-3.5 h-3.5 text-white/30 ml-auto group-hover:text-white/60 transition-colors" />
            </>
          );
          const cls = "group flex items-center gap-2.5 rounded-[14px] bg-white/[0.04] ring-1 ring-white/10 hover:ring-white/25 hover:bg-white/[0.07] px-4 py-3.5 transition-[background-color,box-shadow] duration-200";
          return action ? (
            <button key={key} onClick={action} className={cls} data-testid={`quick-link-${key}`}>{inner}</button>
          ) : (
            <Link key={key} to={to} className={cls} data-testid={`quick-link-${key}`}>{inner}</Link>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Billing preview ---------------- */
function BillingPreview({ transactions, sp, accentColor }) {
  const rows = (transactions || []).slice(0, 3);
  return (
    <div className="bg-[#121212]/60 ring-1 ring-white/10 p-6 md:p-7" data-testid="billing-preview-card">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="font-display font-bold text-lg md:text-xl tracking-tight">Recent payments</h2>
        <Link to={sp(`/account/billing`)} className="text-[11px] uppercase tracking-[0.3em] hover:underline" style={{ color: accentColor }} data-testid="billing-view-all-button">
          View all →
        </Link>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-white/45 py-4" data-testid="billing-preview-empty">No payments yet — your invoices will appear here.</p>
      ) : (
        <div className="divide-y divide-white/8">
          {rows.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-4 py-3" data-testid={`billing-row-${t.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-white capitalize">{(t.kind || "payment").replace("_", " ")}</div>
                <div className="text-[11px] text-white/40">{fmtDate(t.created_at)} · {t.provider}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-white">{formatMoney(t.amount, t.currency)}</div>
                <div className={`text-[10px] uppercase tracking-[0.2em] ${t.status === "succeeded" || t.status === "paid" ? "text-emerald-300" : "text-white/40"}`}>{t.status}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Empty state ---------------- */
function EmptyState({ testid, icon: Icon, title, body, ctaLabel, ctaTestid, onCta, accentColor }) {
  return (
    <div className="relative overflow-hidden ring-1 ring-white/10 bg-[#101011] px-6 py-10 md:py-12" data-testid={testid}>
      <div className="absolute inset-0 -z-10 opacity-70" style={{ background: `radial-gradient(40% 120% at 85% 0%, color-mix(in srgb, ${accentColor} 12%, transparent), transparent 70%)` }} />
      <div className="max-w-md">
        <div className="w-11 h-11 grid place-items-center rounded-full ring-1 ring-white/15 bg-white/5 mb-4">
          <Icon className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight">{title}</h3>
        <p className="mt-2 text-sm text-white/55">{body}</p>
        <Button onClick={onCta} className="mt-5 rounded-none bg-white text-black hover:bg-white/90 uppercase text-[11px] tracking-[0.25em]" data-testid={ctaTestid}>
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Loading skeleton ---------------- */
function AccountSkeleton() {
  return (
    <div data-testid="account-loading-skeleton">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-14">
        <div className="flex items-end gap-6">
          <Skeleton className="h-28 w-28 rounded-md bg-white/10" />
          <div className="space-y-3">
            <Skeleton className="h-4 w-40 bg-white/10" />
            <Skeleton className="h-12 w-72 bg-white/10" />
            <Skeleton className="h-4 w-52 bg-white/10" />
          </div>
        </div>
        <div className="mt-8 flex gap-3">
          <Skeleton className="h-12 w-48 bg-white/10" />
          <Skeleton className="h-12 w-44 bg-white/10" />
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-8">
        <Skeleton className="h-64 w-full bg-white/10" />
        <Skeleton className="h-64 w-full bg-white/10" />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-8 flex gap-4">
        {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-52 w-36 bg-white/10 flex-shrink-0" />)}
      </div>
    </div>
  );
}
